/**
 * SupportFormModal - Support ticket submission modal
 *
 * Features:
 * - Pre-filled user info (name, email, phone)
 * - Category and priority selection
 * - Optional unit selection
 * - File uploads (images up to 10MB, videos up to 1GB)
 * - Sends to support@hollywoodtrucks.com with CC to eric.richner@hollywoodtrucks.com
 * - Urgent tickets also CC andre.champagne@hollywoodtrucks.com
 * - Auto-retry on failure with mailto fallback
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select';
import { useRBAC } from '@/contexts/RBACContext';
import { useAuth } from '@/contexts/AuthContext';
import { getProfileDetails, getAdminUnits, submitSupportTicket } from '@/lib/adminApi';
import { toast } from 'react-toastify';
import {
  Loader2,
  Upload,
  X,
  CheckCircle2,
  AlertCircle,
  FileVideo,
  Mail,
  Send,
} from 'lucide-react';

// Support categories with their codes
const SUPPORT_CATEGORIES = [
  { value: 'technical', label: 'Technical Issue', code: 'T', requiresPhone: true },
  { value: 'hardware', label: 'Hardware Issue', code: 'H', requiresPhone: true },
  { value: 'feature', label: 'Feature Request', code: 'F', requiresPhone: false },
  { value: 'billing', label: 'Account/Billing', code: 'B', requiresPhone: false },
  { value: 'general', label: 'General Question', code: 'G', requiresPhone: false },
  { value: 'urgent', label: 'Urgent/Emergency', code: 'E', requiresPhone: true },
] as const;

// Priority levels
const PRIORITY_LEVELS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
] as const;

// File size limits
const IMAGE_MAX_SIZE = 10 * 1024 * 1024; // 10MB
const VIDEO_MAX_SIZE = 1024 * 1024 * 1024; // 1GB

interface SupportFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedUnitId?: string;
}

interface FileWithPreview {
  file: File;
  preview: string;
  type: 'image' | 'video';
}

type SubmissionState = 'idle' | 'submitting' | 'success' | 'error';

export function SupportFormModal({ open, onOpenChange, preselectedUnitId }: SupportFormModalProps) {
  const location = useLocation();
  const { user: authUser } = useAuth();
  const { profile: _profile, company, companies: _companies, role, memberships } = useRBAC();

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [category, setCategory] = useState<string>('');
  const [priority, setPriority] = useState<string>('medium');
  const [unitId, setUnitId] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<FileWithPreview[]>([]);

  // UI state
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [units, setUnits] = useState<Array<{ id: string; unit_code: string; name: string }>>([]);
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [ticketId, setTicketId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);

  // Determine if phone is required based on category
  const selectedCategory = SUPPORT_CATEGORIES.find(c => c.value === category);
  const phoneRequired = selectedCategory?.requiresPhone ?? false;

  // Load profile details on mount
  useEffect(() => {
    if (!open) return;

    const loadProfile = async () => {
      setLoadingProfile(true);
      try {
        const { profile: profileDetails } = await getProfileDetails();
        setFirstName(profileDetails.first_name || '');
        setLastName(profileDetails.last_name || '');
        setPhone(profileDetails.phone || '');
      } catch (error) {
        console.error('Failed to load profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };

    // Set email from auth user
    if (authUser?.email) {
      setEmail(authUser.email);
    }

    loadProfile();
  }, [open, authUser]);

  // Load units for dropdown
  useEffect(() => {
    if (!open || !company?.id) return;

    const loadUnits = async () => {
      setLoadingUnits(true);
      try {
        const { units: unitList } = await getAdminUnits({ companyId: company.id });
        setUnits(unitList.map((u) => ({
          id: u.id,
          unit_code: u.unit_code,
          name: u.name || u.unit_code,
        })));
      } catch (error) {
        console.error('Failed to load units:', error);
      } finally {
        setLoadingUnits(false);
      }
    };

    loadUnits();
  }, [open, company?.id]);

  // Set preselected unit if provided
  useEffect(() => {
    if (preselectedUnitId && open) {
      setUnitId(preselectedUnitId);
    }
  }, [preselectedUnitId, open]);

  // Get browser info
  const getBrowserInfo = useCallback(() => {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';
    let browserVersion = '';

    if (ua.includes('Firefox/')) {
      browserName = 'Firefox';
      browserVersion = ua.split('Firefox/')[1]?.split(' ')[0] || '';
    } else if (ua.includes('Chrome/')) {
      browserName = 'Chrome';
      browserVersion = ua.split('Chrome/')[1]?.split(' ')[0] || '';
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
      browserName = 'Safari';
      browserVersion = ua.split('Version/')[1]?.split(' ')[0] || '';
    } else if (ua.includes('Edge/')) {
      browserName = 'Edge';
      browserVersion = ua.split('Edge/')[1]?.split(' ')[0] || '';
    }

    return `${browserName} ${browserVersion}`.trim();
  }, []);

  // Get access level string
  const getAccessLevel = useCallback(() => {
    if (role === 'superadmin') return 'Superadmin';
    if (role === 'admin') return 'Admin';
    if (role === 'company_admin') return 'Company Admin';
    if (role === 'group_admin') return 'Group Admin';
    return 'User';
  }, [role]);

  // Handle file selection
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    const newFiles: FileWithPreview[] = [];
    const errors: string[] = [];

    for (const file of selectedFiles) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        errors.push(`${file.name}: Only images and videos are allowed`);
        continue;
      }

      if (isImage && file.size > IMAGE_MAX_SIZE) {
        errors.push(`${file.name}: Image must be under 10MB`);
        continue;
      }

      if (isVideo && file.size > VIDEO_MAX_SIZE) {
        errors.push(`${file.name}: Video must be under 1GB`);
        continue;
      }

      newFiles.push({
        file,
        preview: URL.createObjectURL(file),
        type: isImage ? 'image' : 'video',
      });
    }

    if (errors.length > 0) {
      toast.error(errors.join('\n'));
    }

    setFiles(prev => [...prev, ...newFiles]);

    // Reset input
    e.target.value = '';
  }, []);

  // Remove file
  const removeFile = useCallback((index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  // Clean up file previews on unmount
  useEffect(() => {
    return () => {
      files.forEach(f => URL.revokeObjectURL(f.preview));
    };
  }, []);

  // Generate mailto link for fallback
  const generateMailtoLink = useCallback(() => {
    const effectiveUnitId = unitId === 'none' ? '' : unitId;
    const selectedUnit = effectiveUnitId ? units.find(u => u.id === effectiveUnitId) : null;
    const unitInfo = selectedUnit ? `\n\nUnit: ${selectedUnit.unit_code}` : '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    const body = `Name: ${fullName}
Email: ${email}
Phone: ${phone}
Category: ${selectedCategory?.label || category}
Priority: ${priority}
${unitInfo}

Subject: ${subject}

Message:
${message}

---
Troubleshooting Info:
Browser: ${getBrowserInfo()}
Page: ${location.pathname}
Company: ${company?.name || 'N/A'}
Access Level: ${getAccessLevel()}
Time: ${new Date().toISOString()}`;

    const subjectLine = `SUPPORT: ${subject} (${location.pathname}, ${company?.name || 'N/A'})`;

    const to = 'support@hollywoodtrucks.com';
    const cc = category === 'urgent'
      ? 'eric.richner@hollywoodtrucks.com,andre.champagne@hollywoodtrucks.com'
      : 'eric.richner@hollywoodtrucks.com';

    return `mailto:${to}?cc=${cc}&subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(body)}`;
  }, [firstName, lastName, email, phone, category, priority, unitId, subject, message, units, selectedCategory, getBrowserInfo, location.pathname, company, getAccessLevel]);

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!category) {
      toast.error('Please select a category');
      return;
    }

    if (phoneRequired && !phone.trim()) {
      toast.error('Phone number is required for this category');
      return;
    }

    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmissionState('submitting');
    setErrorMessage('');

    // Prepare file data (base64 encode)
    const fileData = await Promise.all(
      files.map(async (f) => {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]); // Remove data URL prefix
          };
          reader.readAsDataURL(f.file);
        });

        return {
          name: f.file.name,
          type: f.file.type,
          size: f.file.size,
          data: base64,
        };
      })
    );

    // Handle "none" value for unit selection
    const effectiveUnitId = unitId === 'none' ? '' : unitId;
    const selectedUnit = effectiveUnitId ? units.find(u => u.id === effectiveUnitId) : null;

    // Get group info from memberships
    const primaryMembership = memberships.find(m => m.company_id === company?.id && m.group_id);
    const groupCode = primaryMembership?.group_name?.substring(0, 4).toUpperCase() || 'HTFL';
    const siteCode = company?.slug?.substring(0, 2).toUpperCase() || 'HT';

    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const ticketData = {
      // User info
      first_name: firstName,
      last_name: lastName,
      name: fullName, // For backwards compatibility with email templates
      email,
      phone,

      // Ticket details
      category,
      categoryCode: selectedCategory?.code || 'G',
      priority,
      subject,
      message,

      // Unit info
      unitId: effectiveUnitId || null,
      unitCode: selectedUnit?.unit_code || null,

      // Troubleshooting info
      browser: getBrowserInfo(),
      page: location.pathname,
      companyId: company?.id || null,
      companyName: company?.name || null,
      companySlug: company?.slug || null,
      accessLevel: getAccessLevel(),

      // For ticket ID generation
      groupCode,
      siteCode,
      userInitials: [firstName[0], lastName[0]].filter(Boolean).map(c => c.toUpperCase()).join('').substring(0, 2),

      // Attachments
      attachments: fileData,
    };

    // Retry logic with exponential backoff
    const maxRetries = 4;
    const delays = [2000, 4000, 8000, 16000];

    const attemptSubmit = async (attempt: number): Promise<void> => {
      try {
        const result = await submitSupportTicket(ticketData);
        setTicketId(result.ticketId);
        setSubmissionState('success');
        setRetryCount(0);
      } catch (error) {
        console.error(`Submit attempt ${attempt + 1} failed:`, error);

        if (attempt < maxRetries - 1) {
          setRetryCount(attempt + 1);
          await new Promise(resolve => setTimeout(resolve, delays[attempt]));
          return attemptSubmit(attempt + 1);
        }

        // All retries exhausted
        setSubmissionState('error');
        setErrorMessage(error instanceof Error ? error.message : 'Failed to submit support request');
      }
    };

    await attemptSubmit(0);
  };

  // Reset form
  const resetForm = useCallback(() => {
    setCategory('');
    setPriority('medium');
    setUnitId('');
    setSubject('');
    setMessage('');
    setFiles([]);
    setSubmissionState('idle');
    setTicketId('');
    setErrorMessage('');
    setRetryCount(0);
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    if (submissionState === 'success') {
      resetForm();
    }
    onOpenChange(false);
  }, [submissionState, resetForm, onOpenChange]);

  // Handle "Send Another"
  const handleSendAnother = useCallback(() => {
    resetForm();
  }, [resetForm]);

  // Memoized content based on submission state
  const content = useMemo(() => {
    if (submissionState === 'success') {
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="h-16 w-16 rounded-full bg-[#8FB560]/20 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-[#8FB560]" />
          </div>
          <h3 className="text-lg font-semibold">Support Request Submitted</h3>
          <p className="text-muted-foreground text-center">
            Your ticket ID is: <span className="font-mono font-bold text-foreground">{ticketId}</span>
          </p>
          <p className="text-sm text-muted-foreground text-center">
            A confirmation email has been sent to {email}
          </p>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
            <Button onClick={handleSendAnother} className="bg-[#8FB560] hover:bg-[#8FB560]/90">
              Send Another
            </Button>
          </div>
        </div>
      );
    }

    if (submissionState === 'error') {
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="h-16 w-16 rounded-full bg-destructive/20 flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="text-lg font-semibold">Submission Failed</h3>
          <p className="text-muted-foreground text-center text-sm">
            {errorMessage || 'Unable to submit your support request after multiple attempts.'}
          </p>
          <div className="flex flex-col items-center gap-3 pt-4 w-full">
            <Button onClick={() => setSubmissionState('idle')} className="w-full max-w-xs">
              Try Again
            </Button>
            <a
              href={generateMailtoLink()}
              className="flex items-center gap-2 text-sm text-[#8FB560] hover:underline"
            >
              <Mail className="h-4 w-4" />
              Open in Email App
            </a>
          </div>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Contact Info Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              disabled={loadingProfile}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Smith"
              disabled={loadingProfile}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            disabled
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">
            Phone {phoneRequired && <span className="text-destructive">*</span>}
          </Label>
          <Input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            required={phoneRequired}
          />
        </div>

        {/* Category and Priority */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Category <span className="text-destructive">*</span></Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {SUPPORT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority..." />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_LEVELS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Unit Selection */}
        <div className="space-y-2">
          <Label>Having issues with a specific unit?</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger>
              <SelectValue placeholder="———" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">———</SelectItem>
              <SelectSeparator />
              {loadingUnits ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                units.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    {unit.unit_code} {unit.name !== unit.unit_code && `- ${unit.name}`}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Subject */}
        <div className="space-y-2">
          <Label htmlFor="subject">Subject <span className="text-destructive">*</span></Label>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description of the issue"
            required
          />
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Label htmlFor="message">Message <span className="text-destructive">*</span></Label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Please describe your issue in detail..."
            className="min-h-[120px]"
            required
          />
        </div>

        {/* File Upload */}
        <div className="space-y-2">
          <Label>Attachments</Label>
          <div className="border border-dashed border-input rounded-md p-4">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
            />
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
            >
              <Upload className="h-8 w-8 mb-2" />
              <span className="text-sm font-medium">Click to upload</span>
              <span className="text-xs mt-1">Images (max 10MB) or Videos (max 1GB)</span>
            </label>
          </div>

          {/* File previews */}
          {files.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {files.map((f, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-video rounded-md overflow-hidden bg-muted">
                    {f.type === 'image' ? (
                      <img
                        src={f.preview}
                        alt={f.file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileVideo className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <p className="text-xs truncate mt-1 text-muted-foreground">{f.file.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submissionState === 'submitting'}
            className="bg-[#8FB560] hover:bg-[#8FB560]/90"
          >
            {submissionState === 'submitting' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {retryCount > 0 ? `Retrying (${retryCount}/4)...` : 'Submitting...'}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Submit Request
              </>
            )}
          </Button>
        </div>
      </form>
    );
  }, [
    submissionState, ticketId, email, errorMessage, handleClose, handleSendAnother,
    generateMailtoLink, handleSubmit, firstName, lastName, loadingProfile, phone, phoneRequired,
    category, priority, unitId, loadingUnits, units, subject, message, files,
    handleFileChange, removeFile, retryCount,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Contact Support
          </DialogTitle>
          <DialogDescription>
            Fill out the form below and we'll get back to you as soon as possible.
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}

export default SupportFormModal;
